import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { ReaderPosition, ReaderPositionSetInput, ReadingPositionValue } from '@xdocs/schemas';
import { useAuth } from '../../auth/AuthProvider.js';
import { useWs } from '../../ws/WsProvider.js';
import { getStoredReaderPosition, setStoredReaderPosition } from './readerIndexedDb.js';

export interface UseReaderPositionResult {
  /** Resume position for THIS version, or `null` if none is recorded yet —
   * or the only recorded position belongs to a different version/language
   * (F66 AC: "per-VERSION resume position when switching version/language",
   * so a stale offset from another version is never misapplied here). */
  position: ReadingPositionValue | null;
  isLoading: boolean;
  /** True while resolved from the local IndexedDB mirror because the
   * socket isn't connected (E20 §5.10-3 offline resume). */
  isOffline: boolean;
  savePosition: (position: ReadingPositionValue) => void;
}

const SAVE_DEBOUNCE_MS = 800;
const ANONYMOUS_USER_KEY = 'anonymous';

function readerPositionQueryKey(userKey: string, documentId: string): readonly [string, string, string, string] {
  return ['user', userKey, 'readerPosition', documentId] as const;
}

/**
 * F66 resume position (E20 §5.10-3): reads/writes `reader.position` — server-
 * authoritative when connected, mirrored to a local IndexedDB store for
 * instant offline resume and as the fallback source while offline/loading.
 * `savePosition` debounces + writes through `WsClient.enqueueOffline`
 * (§5.10-4: offline-safe optimistic action, durably queued + flushed in
 * order on reconnect — never lost, never double-applied, since the queued
 * item's id is the WS `req` idempotency token).
 */
export function useReaderPosition(documentId: string, versionId: string | null): UseReaderPositionResult {
  const { wsClient, connectionState } = useWs();
  const { userId } = useAuth();
  const queryClient = useQueryClient();
  const connected = connectionState === 'connected';
  const userKey = userId ?? ANONYMOUS_USER_KEY;
  const enabled = connected;

  const query = useQuery<ReaderPosition | null>({
    queryKey: readerPositionQueryKey(userKey, documentId),
    queryFn: () => requestTyped(wsClient, 'reader.position.get', { documentId }).then((result) => result.position),
    enabled,
  });

  const [fallback, setFallback] = useState<ReaderPosition | null | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    setFallback(undefined);
    void getStoredReaderPosition(documentId).then((stored) => {
      if (cancelled) return;
      setFallback(
        stored
          ? { userId: userKey, documentId: stored.documentId, versionId: stored.versionId, position: stored.position, updatedAt: new Date(stored.updatedAt) }
          : null,
      );
    });
    return () => {
      cancelled = true;
    };
  }, [documentId, userKey]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const savePosition = useCallback(
    (next: ReadingPositionValue) => {
      if (!versionId) return;
      const updatedAt = new Date();
      void setStoredReaderPosition({ documentId, versionId, position: next, updatedAt: updatedAt.toISOString() });
      queryClient.setQueryData<ReaderPosition | null>(readerPositionQueryKey(userKey, documentId), () => ({
        userId: userKey,
        documentId,
        versionId,
        position: next,
        updatedAt,
      }));

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const payload: ReaderPositionSetInput = { documentId, versionId, position: next };
        void wsClient.enqueueOffline('reader.position.set', payload).catch(() => {
          // The server settled this as a rejection (not a dropped
          // connection — `enqueueOffline` already retries those
          // transparently on reconnect, E20 §5.10-4). The IndexedDB mirror
          // above keeps local resume correct regardless.
        });
      }, SAVE_DEBOUNCE_MS);
    },
    [documentId, versionId, wsClient, queryClient, userKey],
  );

  const resolved = enabled ? query.data : fallback;
  const isLoading = enabled ? query.isLoading : fallback === undefined;
  const position = resolved && resolved.versionId === versionId ? resolved.position : null;

  return { position, isLoading, isOffline: !connected, savePosition };
}
